import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Tooltip,
  IconButton,
  Dialog,
  Pagination,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Search,
  PlayArrow,
  ShoppingCart,
  Assessment,
  BarChart as BarChartIcon,
  AccessTime,
  Quiz as QuizIcon,
  Replay,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../api";
import { testerHasPermission, isTesterUser } from "../../../utils/testerPermissions";
import useExamMode from "../../../hooks/useExamMode";
import ScanUploadDialog from "../../../components/scan/ScanUploadDialog";
import downloadPaperPdf from "../../../components/scan/downloadPaperPdf";
import TargetSchoolFilterSelect from "../components/TargetSchoolFilterSelect";
import CatalogueFilterBar, { VISIBILITY_DEFAULT } from "../components/CatalogueFilterBar";
import { SCHOOL_TYPE_OPTIONS, schoolMatchesCategory } from "../../../utils/schoolCategoryFilter";
import PanelPackOffer from "../components/PanelPackOffer";
import SchoolPackStrip from "../components/SchoolPackStrip";
import { completePanelPurchase, PANEL_PURCHASE_MESSAGES } from "../../../utils/panelPurchase";
import { usePanelBasket } from "../components/PanelBasket";
import { catalogueCountLabel } from "../components/catalogueMetrics";
import useStudentPortalMode from "../../../hooks/useStudentPortalMode";

const STUDENT_BASE = process.env.REACT_APP_STUDENT_PREFIX || "student";

/** Matches backend config/constants.php mock_exam_purchase_status */
const PP_STATUS = {
  NOT_STARTED: 1,
  IN_PROGRESS: 2,
  COMPLETED: 3,
};

const TAB_PAPERS = 0;
const TAB_PURCHASED = 1;

// Same four values and wording as the mock exams catalogue. Sent to the API as
// `ownership`, since the catalogue is paginated and cannot be filtered in the browser.
const SHOW_OPTIONS = [
  { value: "all", label: "All" },
  { value: "available", label: "Free & purchased" },
  { value: "purchased", label: "Purchased" },
  { value: "buy", label: "To buy" },
];

// One line per status, so "no results" never reads as "nothing exists".
const EMPTY_BY_SHOW = {
  all: "No practice papers are currently available",
  available: "No practice papers are ready to start",
  purchased: "No purchased practice papers yet",
  buy: "No practice papers left to buy",
};

const StudentPastPapers = () => {
  // Course + plan students: say which side this list is for (see DashboardViewToggle).
  const { canToggleDashboard, dashboardView, catalogueLocked } = useStudentPortalMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isTestingMode } = useExamMode();
  const isPractice = isTestingMode("past_papers");
  const isTester = isTesterUser();
  const isTesterPracticeMode = isPractice && isTester;

  const tabFromUrl = searchParams.get("tab") === "purchased" ? TAB_PURCHASED : TAB_PAPERS;
  const [tab, setTab] = useState(tabFromUrl);

  useEffect(() => {
    setTab(searchParams.get("tab") === "purchased" ? TAB_PURCHASED : TAB_PAPERS);
  }, [searchParams]);

  const handleTab = (_, v) => {
    setTab(v);
    setSearchParams(v === TAB_PURCHASED ? { tab: "purchased" } : {});
  };

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterSchoolIds, setFilterSchoolIds] = useState([]);
  const [filterSchoolCategory, setFilterSchoolCategory] = useState("independent");
  const [filterRegion, setFilterRegion] = useState("");
  const [filterKind, setFilterKind] = useState("");
  const [schools, setSchools] = useState([]);
  const [purchasing, setPurchasing] = useState(null);
  const { items: basketItems, addItem: addToBasket, clear: clearBasket, basketUi } = usePanelBasket();
  // "All" or only the papers this student has actually paid for.
  // Opens on what the student already has (free + purchased); "To buy" switches to the shop.
  const [showFilter, setShowFilter] = useState("available");
  // Tester-only Active / Inactive filter, applied server-side (the catalogue is paginated).
  const [visibilityFilter, setVisibilityFilter] = useState(VISIBILITY_DEFAULT);
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogPagination, setCatalogPagination] = useState({ current_page: 1, last_page: 1, per_page: 20, total: 0 });
  const inProgressToastShownRef = useRef(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const hasLoadedCatalogRef = useRef(false);
  const normalizedCatalogSearch = search.trim();
  const catalogSearchTerm = normalizedCatalogSearch.length >= 2 ? normalizedCatalogSearch : "";
  const targetSchoolsFilterKey = filterSchoolIds.slice().sort().join(",");

  useEffect(() => {
    api.get("/common/data", { params: { param: "Subjects" } })
      .then((subRes) => setAllSubjects((subRes.data?.data || []).map((s) => s.name).filter(Boolean).sort()))
      .catch(() => {});
    api.get("/student/past-papers/years")
      .then((res) => setYearOptions(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {});
    Promise.all([
      api.get("/common/data", { params: { param: "Schools", linked_to: "past_papers" } }),
      api.get("/common/data", { params: { param: "Schools", linked_to: "practice_papers" } }),
    ])
      .then(([pastRes, practiceRes]) => {
        const lists = [pastRes, practiceRes].flatMap((res) => {
          const list = res.data?.data || res.data || [];
          return Array.isArray(list) ? list : [];
        });
        const byId = new Map();
        lists.forEach((school) => {
          if (school?.id != null) byId.set(String(school.id), school);
        });
        setSchools(Array.from(byId.values()));
      })
      .catch(() => {});
  }, []);

  const fetchCatalog = useCallback(async (page = 1, extraParams = {}) => {
    setCatalogLoading(true);
    setLoadError(false);
    try {
      const res = await api.get("/student/past-papers/catalog", {
        params: { per_page: 20, page, include_completed: 1, ...extraParams },
      });
      const payload = res.data?.data;
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      // Server order matters: papers for the student's own target schools come first.
      setCatalog(list);
      hasLoadedCatalogRef.current = true;
      setCatalogPagination({
        current_page: payload?.current_page ?? page,
        last_page: payload?.last_page ?? 1,
        per_page: payload?.per_page ?? 20,
        total: payload?.total ?? 0,
      });
      if (!inProgressToastShownRef.current && list.some((p) => p.has_purchase && p.purchase_status === PP_STATUS.IN_PROGRESS)) {
        inProgressToastShownRef.current = true;
        toast.info("You have a paper in progress. Resume it from the card below.", {
          toastId: "in-progress-paper",
          autoClose: 5000,
        });
      }
    } catch {
      toast.error("Failed to load practice papers");
      setCatalog([]);
      setLoadError(true);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  // Bumped once a purchase made from this page is recorded, to reload the catalogue.
  const [purchaseRefreshKey, setPurchaseRefreshKey] = useState(0);

  useEffect(() => {
    // Back from Stripe: record the pack/paper first, then load so every covered
    // row already reads Start instead of Buy.
    completePanelPurchase().then((outcome) => {
      if (outcome) {
        (outcome === "cancelled" ? toast.info : toast.success)(PANEL_PURCHASE_MESSAGES[outcome]);
        if (outcome !== "cancelled") clearBasket();
      }
      setPurchaseRefreshKey((k) => k + 1);
    });
  }, [clearBasket]);

  useEffect(() => {
    if (purchaseRefreshKey === 0) return;
    const params = {};
    if (catalogSearchTerm) params.search = catalogSearchTerm;
    if (filterCategory) params.category = filterCategory;
    if (filterSubject) params.subject = filterSubject;
    if (filterYear) params.year = filterYear;
    if (filterSchoolIds.length) params.target_school_ids = filterSchoolIds.join(",");
    if (filterSchoolCategory === "grammar" || filterSchoolCategory === "independent" || filterSchoolCategory === "both") {
      params.school_category = filterSchoolCategory;
    }
    if (filterRegion) params.region_id = filterRegion;
    if (filterKind) params.practice_kind = filterKind;
    // Server-side: the catalogue is paginated, so filtering "purchased" here in the
    // browser would only ever search the twenty papers currently on screen.
    if (showFilter !== "all") params.ownership = showFilter;
    if (isTester && visibilityFilter) params.visibility = visibilityFilter;
    fetchCatalog(catalogPage, params);
  }, [fetchCatalog, catalogPage, catalogSearchTerm, filterCategory, filterSubject, filterYear, filterSchoolIds, targetSchoolsFilterKey, filterSchoolCategory, filterRegion, filterKind, showFilter, isTester, visibilityFilter, purchaseRefreshKey]);

  useEffect(() => {
    setCatalogPage(1);
  }, [search, filterCategory, filterSubject, filterYear, targetSchoolsFilterKey, filterSchoolCategory, filterRegion, filterKind, showFilter, visibilityFilter]);

  // Deep link from the website (My Purchases → Start on a PDF pack): start that paper and open it.
  // The API refuses a paper this student cannot open, so the link grants nothing by itself.
  const startHandledRef = useRef(null);
  useEffect(() => {
    const startId = String(new URLSearchParams(location.search || "").get("startPastPaper") || "").replace(/[^0-9]/g, "");
    if (!startId || startHandledRef.current === startId) return;
    startHandledRef.current = startId;
    api
      .post(`/student/paper/${startId}/start`)
      .then((res) => {
        const pid = res.data?.data?.purchase_id ?? res.data?.purchase_id;
        if (pid) navigate(`/${STUDENT_BASE}/past-paper/${pid}/take`, { replace: true });
        else toast.error("Could not start paper");
      })
      .catch((e) => toast.error(e.response?.data?.message || "Could not start paper"));
  }, [location.search, navigate]);

  useEffect(() => {
    const focusId = new URLSearchParams(location.search || "").get("focusPastPaper");
    if (!focusId || tab !== TAB_PAPERS || catalogLoading || catalog.length === 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const safeId = String(focusId).replace(/[^0-9]/g, "");
      if (!safeId) return;
      const el = document.querySelector(`[data-past-paper-id="${safeId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [location.search, tab, catalogLoading, catalog]);

  // Both shelves load together (practice papers first). The names still have to come from
  // the category list: a page of practice papers would otherwise hide "Past Papers" as a choice.
  const [shelfCategoryNames, setShelfCategoryNames] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api
      .get("paper/categories")
      .then((res) => {
        if (cancelled) return;
        const roots = Array.isArray(res.data?.data) ? res.data.data : [];
        setShelfCategoryNames(
          roots
            .map((c) => String(c?.name || "").trim())
            .filter((name) => /^(past|practice) papers$/i.test(name))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const names = new Set(shelfCategoryNames);
    catalog.forEach((p) => {
      // Mock exams are a different shelf. A paper row tagged "Mock Exam" must not
      // appear here as a category the student can pick.
      if (p.category && !/mock exam/i.test(p.category)) names.add(p.category);
    });
    return Array.from(names)
      .filter((name) => !/mock exam/i.test(name))
      .sort()
      .map((name) => ({ id: name, name }));
  }, [catalog, shelfCategoryNames]);

  const schoolsOfType = useMemo(
    () => schools.filter((school) => schoolMatchesCategory(school, filterSchoolCategory)),
    [schools, filterSchoolCategory]
  );

  const areaOptions = useMemo(() => {
    const pick = (idKey, labelOf) => {
      const byId = new Map();
      schoolsOfType.forEach((s) => {
        if (s[idKey] && !byId.has(String(s[idKey]))) byId.set(String(s[idKey]), { value: String(s[idKey]), label: labelOf(s) });
      });
      return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
    };
    return {
      regions: pick("region_id", (s) => s.region),
    };
  }, [schoolsOfType]);

  const subjectOptions = useMemo(() => {
    if (allSubjects.length > 0) return allSubjects;
    const names = new Set();
    catalog.forEach((p) => {
      (p.subjects ?? []).forEach((s) => { if (s) names.add(s); });
    });
    return Array.from(names).sort();
  }, [allSubjects, catalog]);

  // Ownership is filtered by the API (see the fetch effect); this only drops rows the
  // catalogue should never show.
  // Purchased-first ordering is the API's job now (orderByIdsFirst in pastPapersCatalog):
  // sorting here could only reach the 20 rows of the current page, which left free
  // previews ahead of a paper the buyer had paid for.
  // Inactive papers only ever reach a tester (the API filters them for everyone else), so a row
  // flagged is_inactive must stay — dropping it here hid tester previews entirely.
  const filteredCatalog = catalog.filter((p) => {
    if (/mock exam/i.test(p.category || "")) return false;
    return Number(p.status) === 2 || p.status == null || p.is_inactive;
  });

  const [resumeDialog, setResumeDialog] = useState(null); // { purchaseId, paperName }
  const [resetting, setResetting] = useState(false);

  const openResumeDialog = (purchaseId, paperName) => {
    setResumeDialog({ purchaseId, paperName });
  };

  const handleContinue = () => {
    if (!resumeDialog) return;
    const id = resumeDialog.purchaseId;
    setResumeDialog(null);
    navigate(`/${STUDENT_BASE}/past-paper/${id}/take`);
  };

  const handleStartFresh = async () => {
    if (!resumeDialog) return;
    const id = resumeDialog.purchaseId;
    setResetting(true);
    try {
      const res = await api.post(`/student/paper/${id}/reset`);
      const newId = res.data?.data?.purchase_id ?? id;
      try { localStorage.removeItem(`paper_draft_${id}`); } catch {}
      setResumeDialog(null);
      await fetchCatalog(catalogPage);
      navigate(`/${STUDENT_BASE}/past-paper/${newId}/take`, { state: { startFresh: true } });
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not reset paper");
    } finally {
      setResetting(false);
    }
  };

  const [showPracticeResults, setShowPracticeResults] = useState(false);
  const [paperSummaries, setPaperSummaries] = useState([]);
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [resultSearch, setResultSearch] = useState("");
  const [resultSearchSubmitted, setResultSearchSubmitted] = useState("");
  const [summaryPagination, setSummaryPagination] = useState({ current_page: 1, last_page: 1, per_page: 25, total: 0 });
  const normalizedResultSearch = resultSearch.trim();

  useEffect(() => {
    if (isTesterPracticeMode && !showPracticeResults) {
      setShowPracticeResults(true);
      setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
    } else if (!isTesterPracticeMode && showPracticeResults) {
      setShowPracticeResults(false);
      setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
    }
  }, [isTesterPracticeMode, showPracticeResults]);

  const fetchPaperSummaries = useCallback(async (page = 1, searchTerm = "", includeTest = false, schoolIds = []) => {
    setSummariesLoading(true);
    try {
      const params = { page, per_page: 25 };
      if (searchTerm) params.search = searchTerm;
      if (includeTest) params.include_test = 1;
      if (schoolIds?.length) params.target_school_ids = schoolIds.join(",");
      const res = await api.get("/student/paper/results-by-paper", { params });
      const payload = res.data?.data ?? res.data;
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setPaperSummaries(list);
      setSummaryPagination({
        current_page: payload?.current_page ?? 1,
        last_page: payload?.last_page ?? 1,
        per_page: payload?.per_page ?? 25,
        total: payload?.total ?? 0,
      });
    } catch {
      toast.error("Failed to load results");
      setPaperSummaries([]);
    } finally {
      setSummariesLoading(false);
    }
  }, []);

  // Same as the mock-exam list: prefetch the results so the Result tab is populated the moment it is
  // opened, instead of starting the request on the tab switch.
  const resultSchoolIds = useMemo(() => {
    if (filterSchoolIds.length) return filterSchoolIds;
    if (!filterSchoolCategory || !schools.length) return [];
    return schoolsOfType.map((school) => String(school.id));
  }, [filterSchoolIds, filterSchoolCategory, schools.length, schoolsOfType]);

  useEffect(() => {
    fetchPaperSummaries(summaryPagination.current_page, resultSearchSubmitted, showPracticeResults, resultSchoolIds);
  }, [resultSearchSubmitted, summaryPagination.current_page, showPracticeResults, resultSchoolIds, fetchPaperSummaries]);

  const handleSchoolCategoryChange = (value) => {
    setFilterSchoolCategory(value);
    setFilterSchoolIds((ids) =>
      ids.filter((id) => {
        const school = schools.find((s) => String(s.id) === String(id));
        return school && schoolMatchesCategory(school, value);
      })
    );
    setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleSchoolFilterChange = (ids) => {
    setFilterSchoolIds(ids);
    setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleClearFilters = () => {
    setSearch("");
    setFilterCategory("");
    setFilterSubject("");
    setFilterYear("");
    setFilterRegion("");
    setFilterKind("");
    setFilterSchoolCategory("independent");
    setShowFilter("available");
    setVisibilityFilter(VISIBILITY_DEFAULT);
    handleSchoolFilterChange([]);
  };

  const handleResultSearch = (e) => {
    e?.preventDefault();
    setResultSearchSubmitted(normalizedResultSearch.length >= 2 ? normalizedResultSearch : "");
    setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleResultClear = () => {
    setResultSearch("");
    setResultSearchSubmitted("");
    setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const formatResultDate = (iso) => {
    if (!iso) return "N/A";
    try {
      return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return iso;
    }
  };

  const isPaperFree = (paper) => {
    if (paper.is_free_paper) return true;
    const n = parseFloat(String(paper.price ?? "").replace(",", "."));
    return !Number.isNaN(n) && n <= 0;
  };

  const canClaimFree = (paper) => !paper.has_purchase && isPaperFree(paper);

  const handleClaimFree = async (paper) => {
    setPurchasing(paper.id);
    try {
      const res = await api.post("/student/paper/claim-free", { paper_id: paper.id });
      const pid = res.data?.data?.purchase_id ?? res.data?.purchase_id;
      toast.success("Paper added to your account.");
      await fetchCatalog(catalogPage);
      if (pid) {
        navigate(`/${STUDENT_BASE}/past-paper/${pid}/take`);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not add this paper");
    } finally {
      setPurchasing(null);
    }
  };

  // Collect it; the parent authorises and pays for the whole basket in one step
  // from the bar. The price is re-resolved server side at checkout, so a paper
  // does not need a stored Stripe price to be buyable.
  const handlePurchase = (paper) => {
    addToBasket({
      resourceType: "paper",
      resourceId: paper.id,
      name: paper.name,
      price: Number(String(paper.price ?? "").replace(/[^0-9.]/g, "")) || 0,
      priceDisplay: paper.price_display || null,
    });
  };

  const isInBasket = (paper) =>
    basketItems.some(
      (row) => row.resourceType === "paper" && Number(row.resourceId) === Number(paper.id)
    );

  // Opening a paper auto-starts it server side, so PP_STATUS.IN_PROGRESS alone is also true of
  // a paper that was merely glanced at and closed. After a bundle/pack purchase that made every
  // browsed card read "Resume" with nothing to resume, so an attempt only counts as in progress
  // once it has a saved answer (`attempt_has_answers`, from the catalogue payload).
  const isInProgress = (paper) =>
    paper.has_purchase &&
    paper.purchase_status === PP_STATUS.IN_PROGRESS &&
    !paper.purchase_completed_at &&
    paper.attempt_has_answers === true;

  const isCompleted = (paper) =>
    paper.has_purchase && paper.purchase_status === PP_STATUS.COMPLETED;

  const handleRetake = async (paper) => {
    setPurchasing(paper.id);
    try {
      const res = await api.post(`/student/paper/${paper.purchase_id}/reset`);
      const newId = res.data?.data?.purchase_id ?? paper.purchase_id;
      try { localStorage.removeItem(`paper_draft_${paper.purchase_id}`); } catch {}
      await fetchCatalog(catalogPage);
      navigate(`/${STUDENT_BASE}/past-paper/${newId}/take`, { state: { startFresh: true } });
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not retake paper");
    } finally {
      setPurchasing(null);
    }
  };

  const handleVerifierStart = async (paper) => {
    setPurchasing(paper.id);
    try {
      const res = await api.post(`/student/paper/${paper.id}/start`);
      const pid = res.data?.data?.purchase_id ?? res.data?.purchase_id;
      if (!pid) {
        toast.error("Could not start paper");
        return;
      }
      await fetchCatalog(catalogPage);
      navigate(`/${STUDENT_BASE}/past-paper/${pid}/take`);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not start paper");
    } finally {
      setPurchasing(null);
    }
  };

  const canVerifyPaper = testerHasPermission("verify-paper");
  const showInitialCatalogLoader = catalogLoading && !hasLoadedCatalogRef.current;

  const [downloadingPaperId, setDownloadingPaperId] = useState(null);
  const [scanPaper, setScanPaper] = useState(null);
  const [allowPaperDownload, setAllowPaperDownload] = useState(false);
  const [allowScanUpload, setAllowScanUpload] = useState(false);
  const [myTargetSchools, setMyTargetSchools] = useState([]);

  useEffect(() => {
    api.get("/student/settings")
      .then((res) => {
        const data = res.data?.data || {};
        setAllowPaperDownload(data.allow_paper_download === true);
        setAllowScanUpload(data.allow_scan_upload === true);
        setMyTargetSchools(Array.isArray(data.target_schools) ? data.target_schools : []);
      })
      .catch(() => {});
  }, []);

  const handleDownloadPaper = async (paper) => {
    setDownloadingPaperId(paper.id);
    try {
      await downloadPaperPdf(`/student/paper/${paper.id}/download-pdf`, `${paper.name || "paper"}.pdf`);
    } finally {
      setDownloadingPaperId(null);
    }
  };

  const handleScanComplete = (res) => {
    setScanPaper(null);
    fetchCatalog(catalogPage);
    navigate(`/${STUDENT_BASE}/past-paper-report/${res?.subject_id ?? scanPaper?.id}`, {
      state: { scannedSubmission: res },
    });
  };

  if (showInitialCatalogLoader && tab === TAB_PAPERS) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (loadError && tab === TAB_PAPERS) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          gap: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 420 }}>
          Unable to load practice papers. Please check your connection and try again.
        </Alert>
        <Button variant="contained" onClick={fetchCatalog}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Free trial ended with no plan: only items already bought open here. */}
      {catalogueLocked && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: 2, alignItems: "center", flexWrap: { xs: "wrap", sm: "nowrap" } }}
          action={
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/${process.env.REACT_APP_STUDENT_PREFIX || "student"}/choose-plan?plans=1`)}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              Choose a plan
            </Button>
          }
        >
          Your free trial has ended. Choose a plan to unlock past and practice papers again. Anything you bought stays open.
        </Alert>
      )}
      <Tabs
        value={tab}
        onChange={handleTab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab
          label="Paper"
          value={TAB_PAPERS}
          icon={<QuizIcon />}
          iconPosition="start"
        />
        <Tab
          label="Result"
          value={TAB_PURCHASED}
          icon={<BarChartIcon />}
          iconPosition="start"
        />
      </Tabs>

      {isTesterPracticeMode && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
          PRACTICE MODE — Practice freely. Answers will not be graded.
        </Alert>
      )}

      {tab === TAB_PAPERS && (
        <Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {isTesterPracticeMode ? "Practice Papers" : "Available Papers"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isTesterPracticeMode
                ? "Practice mode is active. Your results will not be recorded."
                : canToggleDashboard && dashboardView === "mobile"
                  ? "Your mobile plan library. Every paper here is included in your plan."
                  : canToggleDashboard
                    ? "Your course papers. Buy any other paper to add it to your course side."
                    : "Start the papers you have, or buy any other paper right here."}
            </Typography>
          </Box>

          {!isTesterPracticeMode && <PanelPackOffer resourceType={3} refreshKey={purchaseRefreshKey} />}

          {!isTesterPracticeMode && (
            <SchoolPackStrip
              targetSchools={myTargetSchools}
              refreshKey={purchaseRefreshKey}
              basketItems={basketItems}
              onAddToBasket={addToBasket}
              onShowPapers={(schoolId) => {
                if (!schoolId) return;
                setShowFilter("all");
                handleSchoolFilterChange([schoolId]);
              }}
            />
          )}

          <CatalogueFilterBar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by school, subject or year"
            status={{
              value: showFilter,
              onChange: setShowFilter,
              options: SHOW_OPTIONS,
            }}
            region={
              areaOptions.regions.length > 0
                ? { value: filterRegion, onChange: setFilterRegion, options: areaOptions.regions }
                : undefined
            }
            kind={{
              value: filterKind,
              onChange: setFilterKind,
              options: [
                { value: "full_test", label: "Full practice test" },
                { value: "subject", label: "Practice by subject" },
              ],
            }}
            schoolCategory={{
              value: filterSchoolCategory,
              onChange: handleSchoolCategoryChange,
              options: SCHOOL_TYPE_OPTIONS,
            }}
            category={
              categories.length > 0
                ? {
                    value: filterCategory,
                    onChange: setFilterCategory,
                    options: categories.map((c) => ({ value: c.name, label: c.name })),
                  }
                : undefined
            }
            subject={
              subjectOptions.length > 0
                ? {
                    value: filterSubject,
                    onChange: setFilterSubject,
                    options: subjectOptions.map((s) => ({ value: s, label: s })),
                  }
                : undefined
            }
            year={
              yearOptions.length > 0
                ? {
                    value: filterYear,
                    onChange: setFilterYear,
                    options: yearOptions.map((y) => ({ value: y, label: y })),
                  }
                : undefined
            }
            schools={schoolsOfType}
            visibility={isTester ? { value: visibilityFilter, onChange: setVisibilityFilter } : undefined}
            schoolValue={filterSchoolIds}
            onSchoolChange={handleSchoolFilterChange}
            targetSchools={myTargetSchools}
            schoolLabelId="past-paper-target-school-filter"
            resultCount={filteredCatalog.length}
            totalCount={catalogPagination.total}
            onClearAll={handleClearFilters}
          />

          {filteredCatalog.some((p) => isInProgress(p)) && (
            <Alert
              severity="info"
              icon={false}
              sx={{
                mb: 3,
                borderRadius: 2,
                bgcolor: "#ede9fe",
                color: "#5b21b6",
                border: "1px solid #c4b5fd",
                "& .MuiAlert-message": { fontWeight: 600 },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                You have{" "}
                {filteredCatalog.filter((e) => isInProgress(e)).length === 1 ? "a paper" : "papers"} in progress.
                Resume from the card below to continue where you left off.
              </Typography>
            </Alert>
          )}

          {filteredCatalog.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {EMPTY_BY_SHOW[showFilter] || EMPTY_BY_SHOW.all}
              {search || filterCategory || filterSubject || filterYear || filterSchoolIds.length || filterSchoolCategory !== "independent" || filterRegion || filterKind ? " matching your filters" : " to you"}.
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {filteredCatalog.map((paper) => {
                const isPur = purchasing === paper.id;
                const inProg = isInProgress(paper);
                const qCount = paper.extract_questions_count || 0;
                const countLabel = catalogueCountLabel({
                  subjects: paper.subjects,
                  questionsCount: qCount,
                  sectionsCount: paper.sections_count,
                });

                const packUnlocked = paper.is_purchased || paper.access_reason === "bundle" || paper.mobile_access;
                const hasOwnedAttempt = paper.has_purchase && !!paper.purchase_id;
                const canStartWithoutPurchaseRow = packUnlocked || paper.verifier_access || canVerifyPaper;
                const showPurchasePrimary = !paper.has_purchase && !isPaperFree(paper) && !packUnlocked;
                const showFreePrimary = !paper.has_purchase && !packUnlocked && canClaimFree(paper);
                // Only offer print/scan on papers the student can actually open.
                const canUseScan = paper.has_purchase || isPaperFree(paper)
                  || paper.verifier_access || paper.mobile_access || canVerifyPaper || packUnlocked;

                const primaryConfig = (() => {
                  if (hasOwnedAttempt) {
                    if (inProg) {
                      return {
                        label: "Resume",
                        startIcon: <PlayArrow sx={{ fontSize: 16 }} />,
                        onClick: () => openResumeDialog(paper.purchase_id, paper.name),
                        bg: "linear-gradient(90deg, #667eea, #764ba2)",
                      };
                    }
                    if (isCompleted(paper)) {
                      return {
                        label: isTesterPracticeMode ? "Practice Again" : "Retake",
                        startIcon: <Replay sx={{ fontSize: 16 }} />,
                        onClick: () => handleRetake(paper),
                        bg: "linear-gradient(90deg, #1a1a1a, #3a3a3a)",
                      };
                    }
                    return {
                      label: isTesterPracticeMode ? "Start Practice" : "Start",
                      startIcon: <PlayArrow sx={{ fontSize: 16 }} />,
                      onClick: () => navigate(`/${STUDENT_BASE}/past-paper/${paper.purchase_id}/take`),
                      bg: "linear-gradient(90deg, #1a1a1a, #3a3a3a)",
                    };
                  }
                  if (showFreePrimary) {
                    return {
                      label: "Start free paper",
                      startIcon: <PlayArrow sx={{ fontSize: 16 }} />,
                      onClick: () => handleClaimFree(paper),
                      bg: "linear-gradient(90deg, #16a34a, #15803d)",
                    };
                  }
                  if (canStartWithoutPurchaseRow) {
                    return {
                      label: isTesterPracticeMode ? "Start Practice" : "Start",
                      startIcon: <PlayArrow sx={{ fontSize: 16 }} />,
                      onClick: () => (paper.purchase_id
                        ? navigate(`/${STUDENT_BASE}/past-paper/${paper.purchase_id}/take`)
                        : handleVerifierStart(paper)),
                      bg: "linear-gradient(90deg, #1a1a1a, #3a3a3a)",
                    };
                  }
                  if (showPurchasePrimary) {
                    const inBasket = isInBasket(paper);
                    return {
                      label: inBasket
                        ? "In basket"
                        : paper.price_display
                          ? `Add ${paper.price_display}`
                          : "Add to basket",
                      startIcon: <ShoppingCart sx={{ fontSize: 16 }} />,
                      onClick: () => handlePurchase(paper),
                      bg: "linear-gradient(90deg, #7c3aed, #6d28d9)",
                      disabled: inBasket,
                    };
                  }
                  return null;
                })();

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={paper.id}>
                    <Card
                      data-past-paper-id={paper.id}
                      sx={{
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        maxWidth: 360,
                        mx: "auto",
                        transition: "box-shadow 0.2s",
                        border: inProg ? "2px solid #7c3aed" : "1px solid #e0e0e0",
                        "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
                      }}
                    >
                      <CardContent sx={{ p: 1.5, pb: "10px !important", display: "flex", flexDirection: "column", flex: 1 }}>
                        {inProg && (
                          <Box sx={{ mb: 0.75 }}>
                            <Chip
                              label="In Progress"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 22,
                                bgcolor: "#ede9fe",
                                color: "#5b21b6",
                                border: "1px solid #c4b5fd",
                              }}
                            />
                          </Box>
                        )}

                        {/* Only testers are served Inactive papers: set Inactive in admin, not on sale yet. */}
                        {paper.is_inactive && (
                          <Chip
                            label="Inactive · tester preview"
                            size="small"
                            sx={{ alignSelf: "flex-start", mb: 0.5, fontWeight: 700, fontSize: "0.65rem", height: 20, bgcolor: "#FFF4E0", color: "#B4690E", border: "1px solid #F3D19E" }}
                          />
                        )}
                        <Tooltip title={paper.name || ""} placement="top" enterDelay={400}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.78rem",
                              lineHeight: 1.35,
                              mb: 0.5,
                              minHeight: "3.2em",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {paper.name}
                          </Typography>
                        </Tooltip>

                        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1, alignItems: "center" }}>
                          {countLabel ? (
                          <Tooltip title={countLabel.unit === "Q" ? "Questions in this paper" : "Sections in this paper"}>
                            <Typography variant="caption" color="text.secondary" component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                              <QuizIcon sx={{ fontSize: 13 }} />
                              <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                                {countLabel.value}
                                <Box component="span" sx={{ ml: 0.25 }}>
                                  {countLabel.unit}
                                </Box>
                              </Box>
                            </Typography>
                          </Tooltip>
                          ) : null}
                          {paper.duration_minutes ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                              <AccessTime sx={{ fontSize: 13 }} /> {paper.duration_minutes}m
                            </Typography>
                          ) : null}
                          {isTesterPracticeMode && (
                            <Chip label="Unlimited Practice" size="small" color="info" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} />
                          )}
                          {paper.practice_kind ? (
                            <Chip
                              size="small"
                              label={`${paper.practice_group} · ${paper.practice_kind === "full_test" ? "Full test" : "Subject paper"}`}
                              sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20, maxWidth: "100%", bgcolor: "#EEF0FA", color: "#3B2A9F" }}
                            />
                          ) : null}
                          {(paper.matched_target_schools || []).map((schoolName) => (
                            <Chip
                              key={schoolName}
                              size="small"
                              label={schoolName}
                              color="primary"
                              sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20, maxWidth: "100%" }}
                            />
                          ))}
                          {/* Bought (on its own or in a pack) wins over "Free": a paid paper read as free. */}
                          {paper.is_free_paper && !paper.is_purchased && paper.access_reason !== "bundle" && (
                            <Chip size="small" label="Free" color="success" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} />
                          )}
                          {(paper.is_purchased || paper.access_reason === "bundle") && (
                            <Chip size="small" label="Purchased" color="success" variant="outlined" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} />
                          )}
                          {paper.mobile_access && paper.access_label && !paper.is_free_paper && (
                            <Chip size="small" label={paper.access_label} color="success" variant="outlined" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} />
                          )}
                          {isCompleted(paper) && (
                            <Chip size="small" label="Completed" color="success" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} />
                          )}
                        </Box>

                        <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
                          <Box sx={{ display: "flex", gap: 0.75, alignItems: "stretch" }}>
                            {primaryConfig && (
                              <Button
                                fullWidth
                                size="small"
                                variant="contained"
                                disabled={isPur || Boolean(primaryConfig.disabled)}
                                startIcon={isPur ? <CircularProgress size={14} color="inherit" /> : primaryConfig.startIcon}
                                onClick={primaryConfig.onClick}
                                sx={{
                                  background: primaryConfig.bg,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  py: 0.6,
                                  borderRadius: 1.5,
                                  fontSize: "0.78rem",
                                  "&:hover": { opacity: 0.9 },
                                  "&:disabled": { background: "#ccc", color: "white" },
                                }}
                              >
                                {isPur ? primaryConfig.busyLabel || "Starting…" : primaryConfig.label}
                              </Button>
                            )}
                            {canUseScan && allowPaperDownload && (
                              <Tooltip title="Download this paper to print">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={downloadingPaperId === paper.id}
                                      onClick={() => handleDownloadPaper(paper)}
                                      sx={{ border: "1px solid #e0e0e0", borderRadius: 1.5 }}
                                    >
                                      {downloadingPaperId === paper.id
                                        ? <CircularProgress size={16} />
                                        : <DownloadIcon sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {canUseScan && allowScanUpload && (
                              <Tooltip
                                title={isPractice && !isTester
                                  ? "Scan marking isn't available in practice mode"
                                  : "Upload a photo of the paper you wrote"}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={isPractice && !isTester}
                                    onClick={() => setScanPaper(paper)}
                                    sx={{ border: "1px solid #e0e0e0", borderRadius: 1.5 }}
                                  >
                                    <CloudUploadIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {catalogPagination.last_page > 1 && (
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", justifyContent: "space-between", gap: 1, mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {(catalogPagination.current_page - 1) * catalogPagination.per_page + 1}–
                {Math.min(catalogPagination.current_page * catalogPagination.per_page, catalogPagination.total)} of {catalogPagination.total} papers
              </Typography>
              <Pagination
                count={catalogPagination.last_page}
                page={Math.min(catalogPage, catalogPagination.last_page)}
                disabled={catalogLoading}
                onChange={(_, page) => {
                  setCatalogPage(page);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                color="primary"
                shape="rounded"
                siblingCount={0}
                boundaryCount={1}
              />
            </Box>
          )}
        </Box>
      )}

      {tab === TAB_PURCHASED && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                My Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One row per paper with your average score. Open Report to see every attempt.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3, alignItems: "center" }}>
              <Box component="form" onSubmit={handleResultSearch} sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search results"
                  value={resultSearch}
                  onChange={(e) => setResultSearch(e.target.value)}
                  sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 220 }, maxWidth: { xs: '100%', sm: 'none' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button type="submit" variant="contained" size="medium">Search</Button>
              </Box>
              {resultSearchSubmitted && (
                <Button variant="outlined" size="small" onClick={handleResultClear}>Clear</Button>
              )}
              {schoolsOfType.length > 0 && (
                <TargetSchoolFilterSelect
                  schools={schoolsOfType}
                  value={filterSchoolIds}
                  onChange={handleSchoolFilterChange}
                  labelId="past-paper-result-target-school-filter"
                  targetSchools={myTargetSchools}
                  category={filterSchoolCategory}
                />
              )}
              {isTesterPracticeMode && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                  <Typography variant="body2" color="text.secondary">Show practice results</Typography>
                  <input
                    type="checkbox"
                    checked={showPracticeResults}
                    onChange={(e) => {
                      setShowPracticeResults(e.target.checked);
                      setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
                    }}
                  />
                </Box>
              )}
            </Box>

            {summariesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : paperSummaries.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, border: "2px dashed", borderColor: "divider", borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  No Results Found
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Complete a paper to see your results here.
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Paper</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last completed</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Attempts</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Latest score</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paperSummaries.map((row) => (
                        <TableRow key={row.paper_id}>
                          <TableCell sx={{ fontWeight: 500 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.paper_name}</Typography>
                              {isTesterPracticeMode && showPracticeResults && (
                                <Chip
                                  size="small"
                                  label="Practice"
                                  color="info"
                                  sx={{ fontWeight: 700, fontSize: "0.65rem", height: 20 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatResultDate(row.last_completed_at)}</Typography>
                            {row.attempts_count > 1 && (
                              <Typography variant="caption" color="text.secondary">{row.attempts_count} attempts</Typography>
                            )}
                          </TableCell>
                          <TableCell>{row.attempts_count}</TableCell>
                          <TableCell>
                            {(() => {
                              const hasAi = row.last_ai_feedback_status === "completed" && row.last_ai_score != null;
                              const pct = hasAi ? row.last_ai_percentage : row.last_percentage;
                              const score = hasAi ? row.last_ai_score : row.last_score;
                              return (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                  <Tooltip title={hasAi ? "AI-adjusted score" : ""}>
                                    <Chip
                                      size="small"
                                      icon={hasAi
                                        ? <Assessment sx={{ fontSize: 13 }} />
                                        : <BarChartIcon sx={{ fontSize: 13 }} />}
                                      label={`${Number(pct ?? 0).toFixed(1)}%`}
                                      color={Number(pct) >= 50 ? "success" : "error"}
                                      sx={{ fontWeight: 600, alignSelf: "flex-start" }}
                                    />
                                  </Tooltip>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                                    {Number(score ?? 0).toFixed(1)} / {Number(row.last_total_marks ?? 0).toFixed(0)} marks
                                    {hasAi && <Typography component="span" variant="caption" sx={{ color: "#7c3aed", ml: 0.5 }}>(AI)</Typography>}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/${STUDENT_BASE}/past-paper-report/${row.paper_id}`, {
                                state: { includePracticeResults: showPracticeResults },
                              })}
                            >
                              Report
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                  <Button
                    variant="outlined" size="small"
                    disabled={summaryPagination.current_page <= 1}
                    onClick={() => setSummaryPagination((prev) => ({ ...prev, current_page: Math.max(1, prev.current_page - 1) }))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined" size="small"
                    disabled={summaryPagination.current_page >= summaryPagination.last_page}
                    onClick={() => setSummaryPagination((prev) => ({ ...prev, current_page: Math.min(prev.last_page, prev.current_page + 1) }))}
                  >
                    Next
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {scanPaper && (
        <ScanUploadDialog
          open
          onClose={() => setScanPaper(null)}
          title={scanPaper.name}
          endpoints={{
            validate: `/student/paper/${scanPaper.id}/scan/validate`,
            upload: `/student/paper/${scanPaper.id}/scan`,
            status: (id) => `/student/paper/scan/status/${id}`,
          }}
          onComplete={handleScanComplete}
        />
      )}

      <Dialog open={!!resumeDialog} onClose={() => !resetting && setResumeDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Resume or start fresh?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{resumeDialog?.paperName}</strong> has an attempt in progress. Would you like to continue where you left off, or start a fresh attempt?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexDirection: "column", alignItems: "stretch" }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleContinue}
            disabled={resetting}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              background: "linear-gradient(90deg, #667eea, #764ba2)",
              "&:hover": { opacity: 0.9 },
            }}
          >
            Continue where I left off
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={handleStartFresh}
            disabled={resetting}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {resetting ? <CircularProgress size={18} color="inherit" /> : "Start fresh (clears previous answers)"}
          </Button>
          <Button fullWidth onClick={() => setResumeDialog(null)} disabled={resetting} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {basketUi}
    </Box>
  );
};

export default StudentPastPapers;
